import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  GraduationCap, CreditCard, Check, Loader2, AlertCircle, Eye, EyeOff, ArrowRight, Shield, Clock
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Subscribe() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [pricing, setPricing] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [invalidLink, setInvalidLink] = useState(false);

  // Onboarding form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [role, setRole] = useState('');
  const [province, setProvince] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Tax (only for non-trial)
  const [provinces, setProvinces] = useState([]);
  const [taxPreview, setTaxPreview] = useState(null);

  // Post-payment polling (only for non-trial)
  const [pollStatus, setPollStatus] = useState(null);

  // Trial success state
  const [trialSuccess, setTrialSuccess] = useState(false);

  const isTrial = pricing?.trial_days > 0;

  // Fetch pricing info + provinces.
  // Skip validation when returning from Stripe (?session_id=...) — the invite
  // token has already been consumed by checkout and would 404 here, causing
  // the "Invalid Invitation" screen to flash before the payment poller redirects.
  useEffect(() => {
    if (!token) return;
    if (searchParams.get('session_id')) {
      setLoadingPrice(false);
      return;
    }
    Promise.all([
      axios.get(`${API_URL}/billing/invite/${token}`),
      axios.get(`${API_URL}/billing/provinces`),
    ]).then(([priceRes, provRes]) => {
      setPricing(priceRes.data);
      setProvinces(provRes.data);
      setLoadingPrice(false);
    }).catch(() => { setInvalidLink(true); setLoadingPrice(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-calculate tax when province or pricing changes (non-trial only)
  useEffect(() => {
    if (isTrial || !province || !pricing?.amount) { setTaxPreview(null); return; }
    axios.get(`${API_URL}/billing/tax-preview?amount=${pricing.amount}&province=${province}`)
      .then(r => setTaxPreview(r.data))
      .catch(() => setTaxPreview(null));
  }, [province, pricing, isTrial]);

  // Check for returning from Stripe (non-trial only)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && !pollStatus) {
      setPollStatus('checking');
      pollPaymentStatus(sessionId, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 8;
    const interval = 2500;

    if (attempts >= maxAttempts) {
      setPollStatus('timeout');
      return;
    }

    try {
      const { data } = await axios.get(`${API_URL}/billing/checkout-status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setPollStatus('success');
        try {
          const { data: me } = await axios.get(`${API_URL}/auth/me`);
          setUser(me);
        } catch {}
        return;
      } else if (data.status === 'expired') {
        setPollStatus('expired');
        return;
      }
    } catch {}

    setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), interval);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (!province) { setError('Please select your province.'); return; }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/billing/onboard`, {
        token,
        name: name.trim(),
        email: email.trim(),
        password,
        school_name: schoolName.trim(),
        position: role.trim(),
        province,
        origin_url: window.location.origin,
      });

      if (data.trial) {
        // Trial flow — account created, no payment
        setTrialSuccess(true);
        try {
          const { data: me } = await axios.get(`${API_URL}/auth/me`);
          setUser(me);
        } catch {}
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      } else if (data.url) {
        // Stripe flow
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  // Loading state
  if (loadingPrice) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FB' }}>
        <Loader2 size={28} color="#1E2140" style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Invalid link
  if (invalidLink) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FB' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '2rem' }}>
          <AlertCircle size={48} color="#E03131" style={{ marginBottom: '1rem' }} />
          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
            Invalid Invitation
          </h2>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B' }}>
            This link is invalid or has expired. Please contact your administrator for a new invitation.
          </p>
        </div>
      </div>
    );
  }

  // Trial success state
  if (trialSuccess) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FB' }}>
        <div data-testid="trial-success" style={{ textAlign: 'center', maxWidth: 440, padding: '2rem' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', background: '#EBFBEE',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
          }}>
            <Check size={32} color="#2F9E44" />
          </div>
          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
            Welcome aboard!
          </h2>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B' }}>
            Your {pricing?.trial_days}-day evaluation is now active. Redirecting to your dashboard...
          </p>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Post-payment state (non-trial)
  if (pollStatus) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FB' }}>
        <div data-testid="payment-result" style={{ textAlign: 'center', maxWidth: 440, padding: '2rem' }}>
          {pollStatus === 'checking' && (
            <>
              <Loader2 size={48} color="#4C6EF5" style={{ animation: 'spin 1s linear infinite', marginBottom: '1.5rem' }} />
              <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
                Confirming your payment...
              </h2>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B' }}>
                Please wait while we verify your subscription.
              </p>
            </>
          )}
          {pollStatus === 'success' && (
            <>
              <div style={{
                width: 88, height: 88, borderRadius: '50%',
                background: 'linear-gradient(135deg, #EBFBEE 0%, #C3FAE8 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.75rem',
                boxShadow: '0 8px 24px rgba(47, 158, 68, 0.18)',
              }}>
                <Check size={44} color="#2F9E44" strokeWidth={3} />
              </div>
              <h2 data-testid="payment-success-title" style={{
                fontFamily: 'Outfit, sans-serif', fontSize: '1.875rem', fontWeight: 700,
                color: '#1A1D20', marginBottom: '0.5rem', letterSpacing: '-0.01em',
              }}>
                You're in!
              </h2>
              <p style={{
                fontFamily: 'Nunito, sans-serif', fontSize: '1rem', color: '#475569',
                marginBottom: '0.75rem', lineHeight: 1.5,
              }}>
                Your subscription is active. Welcome to FrenchBoard.
              </p>
              <p style={{
                fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#94A3B8',
                marginBottom: '2rem',
              }}>
                A receipt has been emailed to you by Stripe.
              </p>
              <button
                data-testid="lets-get-studying-btn"
                onClick={() => navigate('/dashboard', { replace: true })}
                style={{
                  padding: '0.875rem 2.25rem', borderRadius: '0.75rem',
                  background: '#1E2140', color: 'white', border: 'none',
                  fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 600,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  boxShadow: '0 4px 12px rgba(30, 33, 64, 0.25)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(30, 33, 64, 0.32)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 33, 64, 0.25)';
                }}
              >
                Let's get studying
                <ArrowRight size={18} />
              </button>
            </>
          )}
          {pollStatus === 'expired' && (
            <>
              <AlertCircle size={48} color="#E8590C" style={{ marginBottom: '1.5rem' }} />
              <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
                Payment session expired
              </h2>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B' }}>
                The payment session has expired. Please try again.
              </p>
            </>
          )}
          {pollStatus === 'timeout' && (
            <>
              <AlertCircle size={48} color="#E8590C" style={{ marginBottom: '1.5rem' }} />
              <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.5rem' }}>
                Verification taking longer than expected
              </h2>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B' }}>
                If you completed the payment, please log in and your subscription should be active.
              </p>
              <button onClick={() => navigate('/login')} style={{
                marginTop: '1rem', padding: '0.75rem 2rem', borderRadius: '0.625rem',
                background: '#1E2140', color: 'white', border: 'none',
                fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', fontWeight: 600,
                cursor: 'pointer',
              }}>
                Go to Login
              </button>
            </>
          )}
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  const price = pricing?.amount || 0;
  const currency = (pricing?.currency || 'cad').toUpperCase();
  const trialDays = pricing?.trial_days || 0;

  return (
    <div data-testid="subscribe-page" style={{ display: 'flex', height: '100vh', background: '#F8F9FB' }}>
      {/* Left — Onboarding form */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '2rem', minWidth: 0,
      }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
            <div style={{
              width: 44, height: 44, borderRadius: '0.75rem', background: '#1E2140',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <GraduationCap size={24} color="white" />
            </div>
            <div>
              <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.35rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
                FrenchBoard
              </h1>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B', margin: 0 }}>
                Smartboard Game Engine
              </p>
            </div>
          </div>

          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.75rem', fontWeight: 600, color: '#1A1D20', marginBottom: '0.375rem' }}>
            Get started
          </h2>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B', marginBottom: '1.75rem' }}>
            {isTrial ? (
              <>
                Create your account to begin your{' '}
                <strong style={{ fontFamily: 'Fredoka, sans-serif', color: '#1A1D20' }}>{trialDays}-day</strong>
                {' '}evaluation.
                <span style={{ display: 'block', fontSize: '0.85rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                  ${price} {currency}/mo after trial
                </span>
              </>
            ) : (
              <>
                Create your account.{' '}
                <strong style={{ fontFamily: 'Fredoka, sans-serif', color: '#1A1D20' }}>${price}</strong>
                <span style={{ fontSize: '0.85rem' }}> {currency}/mo + applicable tax</span>
              </>
            )}
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Name</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Full name" required
                data-testid="onboard-name"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#1E2140'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Institution</label>
              <input
                type="text" value={schoolName} onChange={e => setSchoolName(e.target.value)}
                placeholder="School, center, or organization"
                data-testid="onboard-school"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#1E2140'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Your Role</label>
              <input
                type="text" value={role} onChange={e => setRole(e.target.value)}
                placeholder="e.g. Teacher, Coordinator, Director"
                data-testid="onboard-role"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#1E2140'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Province</label>
              <select
                value={province} onChange={e => setProvince(e.target.value)}
                data-testid="onboard-province"
                style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
              >
                <option value="">Select your province</option>
                {provinces.map(p => (
                  <option key={p.code} value={p.code}>{p.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="your@email.ca" required
                data-testid="onboard-email"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#1E2140'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
            </div>

            <div style={{ marginBottom: '1rem', position: 'relative' }}>
              <label style={labelStyle}>Password</label>
              <input
                type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min. 6 characters" required
                data-testid="onboard-password"
                style={{ ...inputStyle, paddingRight: '3rem' }}
                onFocus={e => e.target.style.borderColor = '#1E2140'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{
                position: 'absolute', right: 12, bottom: 12,
                background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '2px',
              }}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Tax breakdown — non-trial only */}
            {!isTrial && taxPreview && (
              <div data-testid="tax-breakdown" style={{
                padding: '0.875rem 1rem', borderRadius: '0.625rem', background: '#F8F9FB',
                border: '1px solid #E2E8F0', marginBottom: '1.25rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                  <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B' }}>Subtotal</span>
                  <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#1A1D20' }}>${taxPreview.base_amount.toFixed(2)}</span>
                </div>
                {taxPreview.tax_type === 'hst' ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B' }}>HST ({taxPreview.hst_rate}%)</span>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#1A1D20' }}>${taxPreview.hst_amount.toFixed(2)}</span>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                      <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B' }}>GST ({taxPreview.gst_rate}%)</span>
                      <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#1A1D20' }}>${taxPreview.gst_amount.toFixed(2)}</span>
                    </div>
                    {taxPreview.pst_amount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B' }}>{taxPreview.pst_label} ({taxPreview.pst_rate}%)</span>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#1A1D20' }}>${taxPreview.pst_amount.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.375rem', borderTop: '1px solid #E2E8F0' }}>
                  <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 700, color: '#1A1D20' }}>Total</span>
                  <span style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#1A1D20' }}>${taxPreview.total.toFixed(2)} {currency}</span>
                </div>
              </div>
            )}

            {error && (
              <div data-testid="onboard-error" style={{
                padding: '0.75rem 1rem', borderRadius: '0.625rem', background: '#FEF2F2',
                color: '#DC2626', fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem',
                marginBottom: '1.25rem', border: '1px solid #FECACA',
              }}>
                {error}
              </div>
            )}

            <button
              type="submit" disabled={submitting}
              data-testid="onboard-submit"
              style={{
                width: '100%', height: '3.25rem', borderRadius: '0.75rem',
                background: submitting ? '#94A3B8' : '#1E2140', color: 'white',
                fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 600,
                border: 'none', cursor: submitting ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                transition: 'background 0.2s',
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Setting up...
                </>
              ) : isTrial ? (
                <>
                  <Clock size={18} />
                  Start Your Evaluation
                  <ArrowRight size={16} />
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Set Up & Subscribe
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.75rem', justifyContent: 'center' }}>
            {isTrial ? (
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8' }}>
                No payment required during evaluation
              </span>
            ) : (
              <>
                <Shield size={13} color="#94A3B8" />
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8' }}>
                  Secure payment via Stripe
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right — Value proposition */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #1E2140 0%, #2C3075 50%, #1E2140 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ textAlign: 'left', padding: '3rem 3.5rem', zIndex: 1, maxWidth: 520 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
            <img src="https://flagcdn.com/48x36/ca.png" alt="" style={{ width: 32, height: 24, objectFit: 'contain' }} />
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Built for Canadian Bilingual Education
            </span>
          </div>

          <h2 style={{
            fontFamily: 'Outfit, sans-serif', fontSize: '2rem', fontWeight: 700,
            color: 'white', marginBottom: '1rem', lineHeight: 1.25,
          }}>
            The smartboard game engine designed for FSL & French Immersion
          </h2>
          <p style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '1rem',
            color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '2.25rem',
          }}>
            Interactive content mapped to 9 provincial curriculums plus CEFR. One consistent platform that travels with the learner.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { title: 'Curriculum continuity', desc: 'Same curriculum map wherever a student practices. Continuous progress across any learning environment' },
              { title: 'Mapped to Canadian standards', desc: 'Tagged to Ontario, BC, Alberta, Saskatchewan, Manitoba, Newfoundland, Nova Scotia, New Brunswick, PEI, and CEFR levels' },
              { title: 'Smartboard-native', desc: 'Built for classroom projection and touch interaction' },
              { title: 'No student data collected', desc: 'Session-based design means no accounts, no privacy friction with school boards' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(74,222,128,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                  <Check size={12} color="#4ADE80" />
                </div>
                <div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 700, color: 'white', marginBottom: '0.125rem' }}>
                    {item.title}
                  </div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ position: 'absolute', bottom: -80, left: -80, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem',
  fontWeight: 600, color: '#1A1D20', marginBottom: '0.375rem',
};

const inputStyle = {
  width: '100%', padding: '0.75rem 1rem', borderRadius: '0.625rem',
  border: '1.5px solid #E2E8F0', fontFamily: 'Nunito, sans-serif',
  fontSize: '0.95rem', outline: 'none', background: 'white',
  transition: 'border-color 0.2s', color: '#1A1D20',
};
